import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import TertiaryLink from '~/components/UI/Buttons/TertiaryLink/TertiaryLink'
import { EventsQuery } from '~/content-types/~queries/news-and-events/EventsQuery'
import { NewsArticlesQuery } from '~/content-types/~queries/news-and-events/NewsQuery'
import { cn } from '~/lib/utils'
import { isDevelopment } from '~/utils/misc'

export type NewsEventsProps = {
  content: {
    stacked?: boolean
    showNews: boolean
    newsThumbs: boolean
    newsCategory?: { entryDescription: string } | null
    showEvents: boolean
    eventThumbs: boolean
    eventsCategory?: { entryDescription: string } | null
    items?: 3 | 4 | 5
  }
  className?: string
  layoutColour: string
}

const NewsEvents = ({ content, className, layoutColour }: NewsEventsProps) => {
  const {
    // stacked,
    showNews = false,
    newsThumbs,
    newsCategory,
    showEvents = false,
    eventThumbs,
    eventsCategory,
    items
  } = content

  const { data: newsArticles } = useQuery({
    queryKey: ['newsArticles'],
    queryFn: () => NewsArticlesQuery(0, newsCategory?.entryDescription, items),
    enabled: showNews
  })
  const { data: events } = useQuery({
    queryKey: ['events'],
    queryFn: () => EventsQuery(0, eventsCategory?.entryDescription, items),
    enabled: showEvents
  })

  const foregroundColour =
    layoutColour === 'ink-blue' || layoutColour === 'mid-blue'
      ? 'white'
      : 'ink-blue'

  return (
    <section
      className={cn(
        '@container/NewsEvents',
        // `${!stacked ? 'lg:flex-row' : ''}`,
        className
      )}
    >
      <div
        className={cn(
          'flex flex-col @min-[57.5rem]/NewsEvents:flex-row',
          `${['ink-blue', 'mid-blue'].includes(layoutColour) ? '[&_a]:hover:text-white' : ''}`
        )}
      >
        {showNews && newsArticles && newsArticles.items.length > 0 && (
          <div
            className={cn(
              'w-full max-w-[1400px]',
              `${['ink-blue', 'mid-blue'].includes(layoutColour) ? 'border-white/40' : 'border-blue-d'}`,
              `${showEvents ? 'pt-m sm:pt-l @min-[57.5rem]/NewsEvents:pr-l pb-m sm:pb-l border-b @min-[57.5rem]/NewsEvents:basis-1/2 @min-[57.5rem]/NewsEvents:border-r @min-[57.5rem]/NewsEvents:border-b-0 @min-[57.5rem]/NewsEvents:pt-0 @min-[57.5rem]/NewsEvents:pb-0' : ''}`

              // `${showEvents && !stacked ? 'lg:pr-l lg:basis-1/2 lg:border-r lg:border-b-0 lg:pb-0' : ''}`,
              // `${showEvents && stacked ? 'lg:max-w-2/3' : ''}`
            )}
          >
            <div className='lg:gap-l gap-m flex flex-col'>
              <div className='mb-xs flex items-center justify-between lg:mb-0'>
                <h2>Latest news</h2>
                <TertiaryLink
                  colour={foregroundColour}
                  data={{
                    externalLink: null,
                    internalLink: {
                      sys: {
                        uri: `/news-and-events/news/${newsCategory ? '?categories=' + newsCategory.entryDescription : ''}`
                      }
                    },
                    ariaLabel: '',
                    newWindow: false,
                    label: 'See all',
                    mobileLabel: ''
                  }}
                  className='h-fit shrink-0'
                />
              </div>
              {newsArticles.items.map((article, index) => (
                <a
                  href={article.sys.uri}
                  key={`article-${index}`}
                  className='unstyled-link sm:gap-m gap-s group flex h-full max-w-[80ch]'
                >
                  {newsThumbs && article.image && (
                    <div className='max-h-xl sm:max-h-2xl aspect-square max-w-xl shrink-0 sm:max-w-2xl lg:max-h-[96px] lg:max-w-[96px]'>
                      <img
                        className='h-full w-full object-cover'
                        src={`${
                          isDevelopment &&
                          !article.image?.asset.sys.uri.startsWith('https')
                            ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
                            : ''
                        }${article.image?.asset.sys.uri}`}
                        alt={article.image?.altText}
                      />
                    </div>
                  )}
                  <div className='sm:gap-xs gap-2xs-plus flex flex-col'>
                    <div className='tag'>
                      {format(article.date, 'dd MMM yyyy')}
                    </div>
                    <div className='lg:text-h4-lg sm:text-h4-md text-base-lg line-clamp-2 font-bold group-hover:underline'>
                      {article.entryTitle}
                    </div>
                    <p className='lg:text-base-lg sm:text-base-md text-base-sm line-clamp-2'>
                      {article.description}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {showEvents && events && events.items?.length > 0 && (
          <div
            className={cn(
              'w-full max-w-[1400px]',
              `${showNews ? 'pt-m sm:pt-l @min-[57.5rem]/NewsEvents:pl-l @min-[57.5rem]/NewsEvents:basis-1/2 @min-[57.5rem]/NewsEvents:pt-0' : ''}`
              // `${showNews && !stacked ? 'lg:pl-l lg:basis-1/2 lg:pt-0' : ''}`,
              // `${showNews && stacked ? 'lg:max-w-2/3' : ''}`
            )}
          >
            <div className='lg:gap-l gap-m flex flex-col'>
              <div className='mb-xs flex items-center justify-between lg:mb-0'>
                <h2>Upcoming events</h2>
                <TertiaryLink
                  colour={foregroundColour}
                  data={{
                    externalLink: null,
                    internalLink: {
                      sys: {
                        uri: `/news-and-events/events/${eventsCategory ? '?cat=' + eventsCategory.entryDescription : ''}`
                      }
                    },
                    ariaLabel: '',
                    newWindow: false,
                    label: 'See all',
                    mobileLabel: ''
                  }}
                  className='h-fit shrink-0'
                />
              </div>

              {events.items.map((event, index) => (
                <a
                  href={event.sys.uri}
                  key={`event-${index}`}
                  className='unstyled-link sm:gap-m gap-s group flex max-w-[80ch]'
                >
                  {eventThumbs && event.image && (
                    <div className='max-h-xl sm:max-h-2xl aspect-square max-w-xl shrink-0 sm:max-w-2xl lg:max-h-[96px] lg:max-w-[96px]'>
                      <img
                        className='h-full w-full object-cover'
                        src={`${
                          isDevelopment &&
                          !event.image?.asset.sys.uri.startsWith('https')
                            ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
                            : ''
                        }${event.image?.asset.sys.uri}`}
                        alt={event.image?.altText}
                      />
                    </div>
                  )}
                  <div className='sm:gap-xs gap-2xs-plus flex flex-col'>
                    <div className='lg:text-h4-lg sm:text-h4-md text-base-lg line-clamp-2 font-bold group-hover:underline'>
                      {event.entryTitle}
                    </div>
                    <div className='tag'>
                      {format(event.date.from, 'dd MMM yyyy')}
                    </div>
                    <p className='lg:text-base-lg sm:text-base-md text-base-sm line-clamp-2'>
                      {event.description}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default NewsEvents
